import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchMyStore } from '../store/storeSlice';
import { uploadMedia, fetchMedia } from '../media/mediaSlice';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import Shell from '../dashboard/Shell';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import FeatureGate from '../../components/FeatureGate';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { navigateToUpgrade } from '../subscription/upgradeNav';

// ─── SVG Icon Components ────────────────────────────────────────────────────────

const GridIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
  </svg>
);

const FlameIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 12c2-2.96 0-7-1-8 0 3.038-1.773 4.741-3 6-1.226 1.26-2 3.24-2 5a6 6 0 1012 0c0-1.532-1.056-3.94-2-5-1.786 3-2.791 3-4 2z" />
  </svg>
);

const TrendingUpIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const EyeIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2.25 12S5.25 5.25 12 5.25 21.75 12 21.75 12 18.75 18.75 12 18.75 2.25 12 2.25 12Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const LockIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="10" width="16" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

const SparklesIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.912 5.813L20 10.5l-4.588 3.687L17.324 20 12 16.5 6.676 20l1.912-5.813L4 10.5l6.088-1.687z" />
  </svg>
);

const BarChartIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" />
    <line x1="6" y1="20" x2="6" y2="16" />
  </svg>
);

const AppearanceIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);

const InfoIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const MegaphoneIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 11l18-5v12L3 13v-2z" /><path d="M11.6 16.8a3 3 0 11-5.8-1.6" />
  </svg>
);

const StarIcon = ({ size = 20, color = 'currentColor', filled = false }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? color : 'none'} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const LinkIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
  </svg>
);

const MailIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const PlusIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const TrashIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);

const EditIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const ChevronDownIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ChevronUpIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

// ─── Shared Styles ──────────────────────────────────────────────────────────────

const sectionHeadingStyle = {
  fontSize: '1.125rem',
  fontWeight: '600',
  color: '#111827',
  marginBottom: '1.5rem',
  paddingBottom: '0.75rem',
  borderBottom: '2px solid #e5e7eb',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: '500',
  color: '#374151',
  marginBottom: '0.5rem',
};

const hintStyle = {
  fontSize: '0.75rem',
  color: '#6b7280',
  marginTop: '0.5rem',
};

const fieldGroupStyle = {
  marginBottom: '1.25rem',
};

const SOCIAL_LINK_FIELDS = [
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourstore or @yourstore' },
  { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/yourstore' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@yourstore or @yourstore' },
  { key: 'twitter', label: 'X / Twitter', placeholder: 'https://x.com/yourstore or @yourstore' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/yourstore' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@yourstore' },
];

// ─── Toggle Switch Component ────────────────────────────────────────────────────

const ToggleSwitch = ({ checked, onChange, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: '44px',
        height: '24px',
        borderRadius: '12px',
        backgroundColor: checked ? '#0d7463' : '#d1d5db',
        position: 'relative',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          backgroundColor: '#fff',
          position: 'absolute',
          top: '2px',
          left: checked ? '22px' : '2px',
          transition: 'left 0.2s ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }}
      />
    </div>
    {label && <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>{label}</span>}
  </div>
);

// ─── Star Rating Component ──────────────────────────────────────────────────────

const StarRating = ({ rating, onChange, readonly = false, size = 22 }) => (
  <div style={{ display: 'flex', gap: '2px', cursor: readonly ? 'default' : 'pointer' }}>
    {[1, 2, 3, 4, 5].map((star) => (
      <span key={star} onClick={() => !readonly && onChange && onChange(star)}>
        <StarIcon size={size} color="#f59e0b" filled={star <= rating} />
      </span>
    ))}
  </div>
);

// ─── Tab Definitions ────────────────────────────────────────────────────────────

const TABS = [
  { key: 'display', label: 'Display' },
  { key: 'announcement', label: 'Announcement' },
  { key: 'reviews', label: 'Reviews' },
  { key: 'footer', label: 'Footer' },
  { key: 'newsletter', label: 'Newsletter' },
];

// ─── Main Component ─────────────────────────────────────────────────────────────

const StorefrontSettings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getAxios } = useSecureAuth();
  const { current: store, status } = useSelector(state => state.store || {});
  const { showToast } = useToast();
  const {
    hasAccess: hasLiveViewerAccess,
    loading: liveViewerAccessLoading,
  } = useFeatureAccess('sales.product_viewer_count');

  const [activeTab, setActiveTab] = useState('display');
  const [loading, setLoading] = useState(false);

  // ─── Display Settings State ─────────────────────────────────────────────────
  const [displaySettings, setDisplaySettings] = useState({
    showAboutPage: true,
    showCategories: false,
    categoriesMax: 8,
    showBestSellers: false,
    bestSellersMax: 4,
    showTrending: false,
    trendingMax: 4,
    showNewArrivals: false,
    newArrivalsMax: 4,
    showReviews: false,
  });

  // Applies to every storefront theme, including the default theme. Light is
  // deliberate: an unconfigured store should not unexpectedly follow a
  // visitor's Dark device setting.
  const [appearanceSettings, setAppearanceSettings] = useState({
    defaultMode: 'light',
    showSwitcher: false,
  });

  const [productViewerCountSettings, setProductViewerCountSettings] = useState({
    enabled: false,
    message: '{count} people are viewing this product',
  });

  // ─── Announcement Settings State ────────────────────────────────────────────
  const [announcementSettings, setAnnouncementSettings] = useState({
    enabled: true,
    type: 'marquee',
    text: 'Free delivery on orders above \u20A610,000!',
    backgroundColor: '#1a1a2e',
    textColor: '#ffffff',
    speed: 30,
  });

  // ─── Reviews State ──────────────────────────────────────────────────────────
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewForm, setReviewForm] = useState(null); // null = hidden, object = form data
  const [editingReviewId, setEditingReviewId] = useState(null);

  // ─── Footer Links State ─────────────────────────────────────────────────────
  const [footerLinks, setFooterLinks] = useState([]);
  const [socialLinks, setSocialLinks] = useState({
    instagram: '',
    facebook: '',
    tiktok: '',
    twitter: '',
    linkedin: '',
    youtube: '',
  });
  const [expandedFooterIndex, setExpandedFooterIndex] = useState(null);

  // ─── Media Picker State (for Newsletter Image) ─────────────────────────────
  const { media: mediaList, uploading: mediaUploading } = useSelector(state => state.media || {});
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const fileInputRef = useRef(null);

  // ─── Newsletter Popup State ─────────────────────────────────────────────────
  const [newsletterSettings, setNewsletterSettings] = useState({
    enabled: true,
    headline: 'Stay in the loop!',
    subtext: 'Get exclusive deals and updates',
    buttonText: 'Subscribe',
    delaySeconds: 10,
    // Layout options
    layout: 'full', // full, split-left, split-right, minimal
    imageUrl: '',
    imageOverlay: false,
    // Styling
    primaryColor: '#0d7463',
    backgroundColor: '#ffffff',
    textColor: '#1e293b',
    borderRadius: 16,
    buttonStyle: 'filled', // filled, outline, rounded
    animation: 'fade', // fade, slide-up, scale
    // Additional fields
    showNameField: false,
    namePlaceholder: 'Your name',
    showPhoneField: false,
    phonePlaceholder: 'Phone number',
    // Discount
    showDiscount: false,
    discountCode: '',
    discountLabel: 'Use code at checkout:',
    // Messages
    successMessage: "You're all set!",
    dismissText: 'No thanks',
    // Behavior
    showOnMobile: true,
    showOncePerSession: true,
  });

  // ─── Fetch store data on mount ──────────────────────────────────────────────
  useEffect(() => {
    if (!store && status !== 'loading') {
      const axiosInstance = getAxios();
      dispatch(fetchMyStore(axiosInstance));
    }
  }, [store, status, dispatch, getAxios]);

  // ─── Load existing settings from store ──────────────────────────────────────
  useEffect(() => {
    if (store?.settings) {
      const s = store.settings;
      if (s.storefrontDisplay) {
        const display = s.storefrontDisplay;
        setDisplaySettings({
          showAboutPage: display.showAboutPage !== false,
          showCategories: display.showCategories === true,
          categoriesMax: display.categoriesMax || 8,
          showBestSellers: display.showBestSellers === true,
          bestSellersMax: display.bestSellersMax || 4,
          showTrending: display.showTrending === true,
          trendingMax: display.trendingMax || 4,
          showNewArrivals: display.showNewArrivals === true,
          newArrivalsMax: display.newArrivalsMax || 4,
          showReviews: display.showReviews === true,
        });
      }
      const appearance = s.storefrontAppearance || {};
      setAppearanceSettings({
        defaultMode: ['light', 'dark', 'system'].includes(appearance.defaultMode)
          ? appearance.defaultMode
          : 'light',
        showSwitcher: appearance.showSwitcher === true,
      });
      setProductViewerCountSettings({
        enabled: s.productViewerCount?.enabled === true,
        message: typeof s.productViewerCount?.message === 'string' && s.productViewerCount.message.trim()
          ? s.productViewerCount.message
          : '{count} people are viewing this product',
      });
      if (s.announcement) {
        setAnnouncementSettings(prev => ({ ...prev, ...s.announcement }));
      }
      if (s.footerLinks) {
        setFooterLinks(s.footerLinks);
      }
      const savedSocialLinks = s.socialLinks || s.socialMedia || {};
      setSocialLinks({
        instagram: savedSocialLinks.instagram || '',
        facebook: savedSocialLinks.facebook || '',
        tiktok: savedSocialLinks.tiktok || '',
        twitter: savedSocialLinks.twitter || '',
        linkedin: savedSocialLinks.linkedin || '',
        youtube: savedSocialLinks.youtube || '',
      });
      if (s.newsletterPopup) {
        setNewsletterSettings(prev => ({ ...prev, ...s.newsletterPopup }));
      }
    }
  }, [store]);

  // ─── Fetch reviews when reviews tab is active ──────────────────────────────
  useEffect(() => {
    if (activeTab === 'reviews') {
      fetchReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchReviews = async () => {
    setReviewsLoading(true);
    try {
      const axiosInstance = getAxios();
      const res = await axiosInstance.get('/store-reviews');
      setReviews(res.data.reviews || res.data || []);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      showToast(err.response?.data?.error || 'Failed to load reviews', 'error');
    } finally {
      setReviewsLoading(false);
    }
  };

  // ─── Display Settings Handlers ──────────────────────────────────────────────
  const handleDisplayChange = (field, value) => {
    const numValue = Math.max(1, Math.min(20, parseInt(value) || 1));
    setDisplaySettings(prev => ({ ...prev, [field]: numValue }));
  };

  const handleDisplayToggle = (field, value) => {
    setDisplaySettings(prev => ({ ...prev, [field]: Boolean(value) }));
  };

  // ─── Announcement Settings Handlers ─────────────────────────────────────────
  const handleAnnouncementChange = (field, value) => {
    setAnnouncementSettings(prev => ({ ...prev, [field]: value }));
  };

  // ─── Newsletter Settings Handlers ───────────────────────────────────────────
  const handleNewsletterChange = (field, value) => {
    setNewsletterSettings(prev => ({ ...prev, [field]: value }));
  };

  // ─── Media Picker Handlers ─────────────────────────────────────────────────
  const openMediaPicker = () => {
    dispatch(fetchMedia({ params: { type: 'image' } }));
    setShowMediaPicker(true);
  };

  const handleMediaSelect = (mediaUrl) => {
    setNewsletterSettings(prev => ({ ...prev, imageUrl: mediaUrl }));
    setShowMediaPicker(false);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const result = await dispatch(uploadMedia({ file })).unwrap();
      if (result?.url) {
        setNewsletterSettings(prev => ({ ...prev, imageUrl: result.url }));
        showToast('Image uploaded successfully', 'success');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      showToast('Failed to upload image', 'error');
    }

    event.target.value = '';
  };

  const removeNewsletterImage = () => {
    setNewsletterSettings(prev => ({ ...prev, imageUrl: '' }));
  };

  const handleSocialLinkChange = (field, value) => {
    setSocialLinks(prev => ({ ...prev, [field]: value }));
  };

  const normalizeSocialLinks = () => {
    return SOCIAL_LINK_FIELDS.reduce((acc, field) => {
      const value = (socialLinks[field.key] || '').trim();
      if (value) acc[field.key] = value;
      return acc;
    }, {});
  };

  // ─── Save settings to store.settings via POST /stores ───────────────────────
  const handleSaveSettings = async () => {
    const viewerMessage = productViewerCountSettings.message?.trim() || '';
    if (productViewerCountSettings.enabled && (!viewerMessage.includes('{count}') || viewerMessage.length > 140)) {
      showToast('Live viewer text must include {count} and be 140 characters or fewer.', 'error');
      return;
    }
    setLoading(true);
    try {
      const axiosInstance = getAxios();
      const updatedSettings = {
        ...(store?.settings || {}),
        storefrontDisplay: displaySettings,
        storefrontAppearance: appearanceSettings,
        productViewerCount: { ...productViewerCountSettings, message: viewerMessage || '{count} people are viewing this product' },
        announcement: announcementSettings,
        footerLinks: footerLinks,
        socialLinks: normalizeSocialLinks(),
        newsletterPopup: newsletterSettings,
      };

      await axiosInstance.post('/stores', {
        ...store,
        settings: updatedSettings,
      });

      await dispatch(fetchMyStore(axiosInstance));
      showToast('Settings saved successfully', 'success');
    } catch (err) {
      console.error('Error saving settings:', err);
      showToast(err.response?.data?.error || 'Failed to save settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ─── Review CRUD Handlers ───────────────────────────────────────────────────
  const openNewReviewForm = () => {
    setEditingReviewId(null);
    setReviewForm({
      customerName: '',
      rating: 5,
      reviewText: '',
      customerImage: '',
    });
  };

  const openEditReviewForm = (review) => {
    setEditingReviewId(review.id);
    setReviewForm({
      customerName: review.customerName || '',
      rating: review.rating || 5,
      reviewText: review.reviewText || '',
      customerImage: review.customerImage || '',
    });
  };

  const closeReviewForm = () => {
    setReviewForm(null);
    setEditingReviewId(null);
  };

  const handleSaveReview = async () => {
    if (!reviewForm.customerName.trim() || !reviewForm.reviewText.trim()) {
      showToast('Customer name and review text are required', 'error');
      return;
    }
    setLoading(true);
    try {
      const axiosInstance = getAxios();
      const payload = {
        customerName: reviewForm.customerName.trim(),
        rating: reviewForm.rating,
        reviewText: reviewForm.reviewText.trim(),
        customerImage: reviewForm.customerImage.trim() || null,
        sortOrder: editingReviewId ? undefined : reviews.length,
      };

      if (editingReviewId) {
        await axiosInstance.put(`/store-reviews/${editingReviewId}`, payload);
        showToast('Review updated successfully', 'success');
      } else {
        await axiosInstance.post('/store-reviews', payload);
        showToast('Review added successfully', 'success');
      }
      closeReviewForm();
      await fetchReviews();
    } catch (err) {
      console.error('Error saving review:', err);
      showToast(err.response?.data?.error || 'Failed to save review', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      const axiosInstance = getAxios();
      await axiosInstance.delete(`/store-reviews/${reviewId}`);
      showToast('Review deleted successfully', 'success');
      await fetchReviews();
    } catch (err) {
      console.error('Error deleting review:', err);
      showToast(err.response?.data?.error || 'Failed to delete review', 'error');
    }
  };

  const handleToggleReviewActive = async (review) => {
    try {
      const axiosInstance = getAxios();
      await axiosInstance.put(`/store-reviews/${review.id}`, {
        ...review,
        isActive: !review.isActive,
      });
      showToast(`Review ${review.isActive ? 'deactivated' : 'activated'}`, 'success');
      await fetchReviews();
    } catch (err) {
      console.error('Error toggling review:', err);
      showToast(err.response?.data?.error || 'Failed to update review', 'error');
    }
  };

  // ─── Footer Links Handlers ─────────────────────────────────────────────────
  const addFooterLink = () => {
    if (footerLinks.length >= 6) {
      showToast('Maximum of 6 footer links allowed', 'error');
      return;
    }
    const newLink = { title: '', content: '', enabled: true };
    setFooterLinks(prev => [...prev, newLink]);
    setExpandedFooterIndex(footerLinks.length);
  };

  const updateFooterLink = (index, field, value) => {
    setFooterLinks(prev => prev.map((link, i) => (i === index ? { ...link, [field]: value } : link)));
  };

  const removeFooterLink = (index) => {
    setFooterLinks(prev => prev.filter((_, i) => i !== index));
    setExpandedFooterIndex(null);
  };

  // ─── Tab Bar ────────────────────────────────────────────────────────────────
  const renderTabBar = () => (
    <div style={{
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '2rem',
      flexWrap: 'wrap',
      backgroundColor: '#f3f4f6',
      padding: '4px',
      borderRadius: '10px',
    }}>
      {TABS.map((tab) => (
        <button
          key={tab.key}
          onClick={() => setActiveTab(tab.key)}
          style={{
            padding: '0.5rem 1.25rem',
            fontSize: '0.875rem',
            fontWeight: activeTab === tab.key ? '600' : '500',
            color: activeTab === tab.key ? '#ffffff' : '#6b7280',
            backgroundColor: activeTab === tab.key ? '#0d7463' : 'transparent',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  // ─── Display Tab ────────────────────────────────────────────────────────────
  const statusPill = (enabled) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '64px',
    padding: '0.25rem 0.625rem',
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 700,
    color: enabled ? '#0d7463' : '#6b7280',
    backgroundColor: enabled ? '#d1fae5' : '#f3f4f6',
  });

  const renderDisplaySectionControl = ({
    icon,
    title,
    description,
    toggleField,
    maxField,
    maxLabel = 'Maximum items',
    maxHint,
  }) => {
    const enabled = displaySettings[toggleField] === true;

    return (
      <Card style={{ marginBottom: '1rem', borderColor: enabled ? '#99f6e4' : '#e5e7eb' }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '1rem',
          marginBottom: enabled && maxField ? '1rem' : 0,
        }}>
          <div style={{ display: 'flex', gap: '0.75rem', minWidth: 0 }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: enabled ? '#ecfdf5' : '#f9fafb',
              border: `1px solid ${enabled ? '#99f6e4' : '#e5e7eb'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              {icon}
            </div>
            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', margin: 0 }}>
                {title}
              </h3>
              <p style={{ ...hintStyle, marginTop: '0.35rem', lineHeight: 1.5 }}>
                {description}
              </p>
            </div>
          </div>
          <ToggleSwitch
            checked={enabled}
            onChange={(val) => handleDisplayToggle(toggleField, val)}
            label={enabled ? 'Enabled' : 'Hidden'}
          />
        </div>

        {maxField && enabled && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(160px, 220px) 1fr',
            gap: '1rem',
            alignItems: 'end',
            paddingTop: '1rem',
            borderTop: '1px solid #f3f4f6',
          }}>
            <div>
              <label style={labelStyle}>{maxLabel}</label>
              <Input
                type="number"
                min="1"
                max="20"
                value={displaySettings[maxField]}
                onChange={(e) => handleDisplayChange(maxField, e.target.value)}
              />
            </div>
            <p style={{ ...hintStyle, margin: 0, lineHeight: 1.5 }}>
              {maxHint}
            </p>
          </div>
        )}
      </Card>
    );
  };

  const renderDisplayTab = () => (
    <>
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={sectionHeadingStyle}>
          <AppearanceIcon size={20} color="#0d7463" /> Storefront Appearance
        </h3>
        <Card style={{ borderColor: appearanceSettings.showSwitcher ? '#99f6e4' : '#e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.75rem', minWidth: 0 }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px',
                backgroundColor: appearanceSettings.showSwitcher ? '#ecfdf5' : '#f9fafb',
                border: `1px solid ${appearanceSettings.showSwitcher ? '#99f6e4' : '#e5e7eb'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <AppearanceIcon size={20} color="#0d7463" />
              </div>
              <div>
                <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', margin: 0 }}>
                  Let customers switch appearance
                </h4>
                <p style={{ ...hintStyle, marginTop: '0.35rem', lineHeight: 1.5 }}>
                  Show a light/dark control in your storefront header. A customer’s choice is saved only for your store.
                </p>
              </div>
            </div>
            <ToggleSwitch
              checked={appearanceSettings.showSwitcher}
              onChange={(showSwitcher) => setAppearanceSettings(prev => ({ ...prev, showSwitcher }))}
              label={appearanceSettings.showSwitcher ? 'Enabled' : 'Hidden'}
            />
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'minmax(180px, 260px) 1fr', gap: '1rem',
            alignItems: 'end', paddingTop: '1rem', marginTop: '1rem', borderTop: '1px solid #f3f4f6',
          }}>
            <div>
              <label htmlFor="storefront-default-appearance" style={labelStyle}>Default appearance</label>
              <select
                id="storefront-default-appearance"
                value={appearanceSettings.defaultMode}
                onChange={(event) => setAppearanceSettings(prev => ({ ...prev, defaultMode: event.target.value }))}
                style={{
                  width: '100%', minHeight: '42px', padding: '0.625rem 0.75rem', borderRadius: '8px',
                  border: '1px solid #d1d5db', background: '#fff', color: '#111827', fontSize: '0.875rem',
                }}
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="system">Match device</option>
              </select>
            </div>
            <p style={{ ...hintStyle, margin: 0, lineHeight: 1.5 }}>
              Light is recommended for most stores. Match device follows each visitor’s phone or computer appearance until they choose a setting using the switcher.
            </p>
          </div>
        </Card>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={sectionHeadingStyle}>
          <EyeIcon size={20} color="#0d7463" /> Live Product Viewers
        </h3>
        <FeatureGate
          featureId="sales.product_viewer_count"
          showUpgradePrompt={false}
          requiredTier={{ tier: 'PREMIUM', name: 'Premium' }}
          fallback={(
            <Card style={{ background: '#fafafa', borderColor: '#e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '10px', background: '#f3f4f6',
                  border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <LockIcon size={19} color="#6b7280" />
                </div>
                <div style={{ minWidth: 0 }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', margin: 0 }}>Premium feature</h4>
                  <p style={{ ...hintStyle, margin: '0.3rem 0 0', lineHeight: 1.5 }}>
                    Let product pages show verified, real-time shopper interest. Available on Premium.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigateToUpgrade(navigate, { tier: 'PREMIUM', name: 'Premium' })}
                  style={{
                    background: '#111827', border: 0, borderRadius: '7px', color: '#fff', cursor: 'pointer',
                    flexShrink: 0, fontSize: '0.8125rem', fontWeight: 700, padding: '0.65rem 0.85rem', whiteSpace: 'nowrap',
                  }}
                >
                  Unlock with Premium
                </button>
              </div>
            </Card>
          )}
        >
          <Card style={{ borderColor: productViewerCountSettings.enabled ? '#99f6e4' : '#e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
              <div style={{ display: 'flex', gap: '0.75rem', minWidth: 0 }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '10px',
                  backgroundColor: productViewerCountSettings.enabled ? '#ecfdf5' : '#f9fafb',
                  border: `1px solid ${productViewerCountSettings.enabled ? '#99f6e4' : '#e5e7eb'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <EyeIcon size={20} color="#0d7463" />
                </div>
                <div>
                  <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', margin: 0 }}>Show live viewers on product pages</h4>
                  <p style={{ ...hintStyle, marginTop: '0.35rem', lineHeight: 1.5 }}>
                    Shows real, anonymous sessions viewing the same product right now. The message stays hidden until at least two people are viewing it—there is no artificial count or sales boost.
                  </p>
                </div>
              </div>
              <ToggleSwitch
                checked={productViewerCountSettings.enabled}
                onChange={(enabled) => setProductViewerCountSettings({ enabled })}
                label={productViewerCountSettings.enabled ? 'Enabled' : 'Hidden'}
              />
            </div>
            <div style={{ ...fieldGroupStyle, marginTop: '1rem', marginBottom: 0 }}>
              <label style={labelStyle}>Message shown to shoppers</label>
              <Input
                type="text"
                maxLength={140}
                value={productViewerCountSettings.message}
                onChange={(event) => setProductViewerCountSettings((current) => ({ ...current, message: event.target.value }))}
                placeholder="{count} people are viewing this product"
                style={{ width: '100%' }}
              />
              <p style={{ ...hintStyle, margin: '0.4rem 0 0', lineHeight: 1.5 }}>
                Include <code>{'{count}'}</code> wherever the live viewer number should appear.
              </p>
            </div>
          </Card>
        </FeatureGate>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={sectionHeadingStyle}>
          <BarChartIcon size={20} color="#0d7463" /> Homepage Sections
        </h3>
        <p style={{ ...hintStyle, marginTop: '-1rem', marginBottom: '1rem', lineHeight: 1.6 }}>
          Choose exactly which optional homepage sections customers see. New stores keep these sections hidden until you enable them.
        </p>
      </div>

      {renderDisplaySectionControl({
        icon: <InfoIcon size={20} color="#0d7463" />,
        title: 'About Page',
        description: 'Show the About page in storefront navigation and make the page accessible to customers.',
        toggleField: 'showAboutPage',
      })}

      {renderDisplaySectionControl({
        icon: <GridIcon size={20} color="#0d7463" />,
        title: 'Shop by Category',
        description: 'Show a category browsing block above the product catalog.',
        toggleField: 'showCategories',
        maxField: 'categoriesMax',
        maxLabel: 'Maximum categories',
        maxHint: `Show up to ${displaySettings.categoriesMax} categories initially. A Show More button appears when more categories exist.`,
      })}

      {renderDisplaySectionControl({
        icon: <FlameIcon size={20} color="#0d7463" />,
        title: 'Best Sellers',
        description: 'Highlight products marked as best sellers on the homepage.',
        toggleField: 'showBestSellers',
        maxField: 'bestSellersMax',
        maxLabel: 'Maximum products',
        maxHint: `Display ${displaySettings.bestSellersMax} best-selling products. Mark products as Best Seller in the product form.`,
      })}

      {renderDisplaySectionControl({
        icon: <TrendingUpIcon size={20} color="#0d7463" />,
        title: 'Trending Products',
        description: 'Show products the merchant wants customers to notice right now.',
        toggleField: 'showTrending',
        maxField: 'trendingMax',
        maxLabel: 'Maximum products',
        maxHint: `Display ${displaySettings.trendingMax} trending products. Mark products as Trending in the product form.`,
      })}

      {renderDisplaySectionControl({
        icon: <SparklesIcon size={20} color="#0d7463" />,
        title: 'New Arrivals',
        description: 'Surface newly added products or products explicitly marked as new arrivals.',
        toggleField: 'showNewArrivals',
        maxField: 'newArrivalsMax',
        maxLabel: 'Maximum products',
        maxHint: `Display ${displaySettings.newArrivalsMax} new arrivals. Products marked as New Arrival or created within the last 30 days can appear here.`,
      })}

      {renderDisplaySectionControl({
        icon: <StarIcon size={20} color="#0d7463" />,
        title: 'What Customers Say',
        description: 'Show approved customer reviews from the Reviews tab on the homepage.',
        toggleField: 'showReviews',
      })}

      {/* Configuration Summary */}
      <Card style={{ backgroundColor: '#f9fafb' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#111827', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <BarChartIcon size={18} color="#6b7280" /> Current Configuration
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', fontSize: '0.875rem' }}>
          <div>
            <span style={{ color: '#6b7280' }}>About Page:</span>
            <span style={{ ...statusPill(displaySettings.showAboutPage), marginLeft: '0.5rem' }}>
              {displaySettings.showAboutPage ? 'Enabled' : 'Hidden'}
            </span>
          </div>
          <div>
            <span style={{ color: '#6b7280' }}>Categories:</span>
            <span style={{ ...statusPill(displaySettings.showCategories), marginLeft: '0.5rem' }}>
              {displaySettings.showCategories ? `${displaySettings.categoriesMax} max` : 'Hidden'}
            </span>
          </div>
          <div>
            <span style={{ color: '#6b7280' }}>Best Sellers:</span>
            <span style={{ ...statusPill(displaySettings.showBestSellers), marginLeft: '0.5rem' }}>
              {displaySettings.showBestSellers ? `${displaySettings.bestSellersMax} max` : 'Hidden'}
            </span>
          </div>
          <div>
            <span style={{ color: '#6b7280' }}>Trending:</span>
            <span style={{ ...statusPill(displaySettings.showTrending), marginLeft: '0.5rem' }}>
              {displaySettings.showTrending ? `${displaySettings.trendingMax} max` : 'Hidden'}
            </span>
          </div>
          <div>
            <span style={{ color: '#6b7280' }}>New Arrivals:</span>
            <span style={{ ...statusPill(displaySettings.showNewArrivals), marginLeft: '0.5rem' }}>
              {displaySettings.showNewArrivals ? `${displaySettings.newArrivalsMax} max` : 'Hidden'}
            </span>
          </div>
          <div>
            <span style={{ color: '#6b7280' }}>Customer Reviews:</span>
            <span style={{ ...statusPill(displaySettings.showReviews), marginLeft: '0.5rem' }}>
              {displaySettings.showReviews ? 'Enabled' : 'Hidden'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <span style={{ color: '#6b7280' }}>Appearance:</span>
            <span style={{ ...statusPill(appearanceSettings.showSwitcher), whiteSpace: 'nowrap' }}>
              {appearanceSettings.defaultMode === 'system'
                ? `System · switcher ${appearanceSettings.showSwitcher ? 'on' : 'off'}`
                : `${appearanceSettings.defaultMode === 'dark' ? 'Dark' : 'Light'} · switcher ${appearanceSettings.showSwitcher ? 'on' : 'off'}`}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <span style={{ color: '#6b7280' }}>Live Product Viewers:</span>
            <span style={statusPill(productViewerCountSettings.enabled && hasLiveViewerAccess && !liveViewerAccessLoading)}>
              {!productViewerCountSettings.enabled
                ? 'Hidden'
                : liveViewerAccessLoading
                  ? 'Checking access…'
                  : hasLiveViewerAccess
                    ? 'Enabled'
                    : 'Locked · Premium required'}
            </span>
          </div>
        </div>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
        <Button variant="primary" disabled={loading} onClick={handleSaveSettings}>
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </>
  );

  // ─── Announcement Tab ───────────────────────────────────────────────────────
  const renderAnnouncementTab = () => {
    const marqueeKeyframes = `
      @keyframes storefront-marquee {
        0% { transform: translateX(100%); }
        100% { transform: translateX(-100%); }
      }
    `;

    return (
      <>
        <Card style={{ marginBottom: '1.5rem' }}>
          <h3 style={sectionHeadingStyle}>
            <MegaphoneIcon size={20} color="#0d7463" /> Announcement Bar
          </h3>

          <div style={fieldGroupStyle}>
            <ToggleSwitch
              checked={announcementSettings.enabled}
              onChange={(val) => handleAnnouncementChange('enabled', val)}
              label="Enable Announcement Bar"
            />
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Display Type</label>
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              {[
                { value: 'marquee', label: 'Marquee (scrolling)' },
                { value: 'static', label: 'Static (centered)' },
              ].map((opt) => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', color: '#374151' }}>
                  <input
                    type="radio"
                    name="announcementType"
                    value={opt.value}
                    checked={announcementSettings.type === opt.value}
                    onChange={(e) => handleAnnouncementChange('type', e.target.value)}
                    style={{ accentColor: '#0d7463' }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Announcement Text</label>
            <Input
              type="text"
              value={announcementSettings.text}
              onChange={(e) => handleAnnouncementChange('text', e.target.value)}
              placeholder="Enter your announcement message..."
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
            <div>
              <label style={labelStyle}>Background Color</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <input
                  type="color"
                  value={announcementSettings.backgroundColor}
                  onChange={(e) => handleAnnouncementChange('backgroundColor', e.target.value)}
                  style={{ width: '40px', height: '40px', border: '2px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', padding: '2px' }}
                />
                <Input
                  type="text"
                  value={announcementSettings.backgroundColor}
                  onChange={(e) => handleAnnouncementChange('backgroundColor', e.target.value)}
                  style={{ maxWidth: '120px', fontFamily: 'monospace', fontSize: '0.875rem' }}
                />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Text Color</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <input
                  type="color"
                  value={announcementSettings.textColor}
                  onChange={(e) => handleAnnouncementChange('textColor', e.target.value)}
                  style={{ width: '40px', height: '40px', border: '2px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', padding: '2px' }}
                />
                <Input
                  type="text"
                  value={announcementSettings.textColor}
                  onChange={(e) => handleAnnouncementChange('textColor', e.target.value)}
                  style={{ maxWidth: '120px', fontFamily: 'monospace', fontSize: '0.875rem' }}
                />
              </div>
            </div>
          </div>

          {announcementSettings.type === 'marquee' && (
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>
                Scroll Speed: {announcementSettings.speed}s (one full cycle)
              </label>
              <input
                type="range"
                min="10"
                max="60"
                value={announcementSettings.speed}
                onChange={(e) => handleAnnouncementChange('speed', parseInt(e.target.value))}
                style={{ width: '100%', accentColor: '#0d7463' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#9ca3af' }}>
                <span>10s (Fast)</span>
                <span>60s (Slow)</span>
              </div>
            </div>
          )}

          {/* Live Preview */}
          <div style={{ marginTop: '1.5rem' }}>
            <label style={{ ...labelStyle, marginBottom: '0.75rem' }}>Live Preview</label>
            <div
              style={{
                backgroundColor: announcementSettings.backgroundColor,
                color: announcementSettings.textColor,
                padding: '0.625rem 0',
                borderRadius: '8px',
                overflow: 'hidden',
                fontSize: '0.875rem',
                fontWeight: '500',
                position: 'relative',
              }}
            >
              {announcementSettings.type === 'marquee' ? (
                <>
                  <style>{marqueeKeyframes}</style>
                  <div
                    style={{
                      whiteSpace: 'nowrap',
                      animation: `storefront-marquee ${announcementSettings.speed}s linear infinite`,
                      display: 'inline-block',
                    }}
                  >
                    {announcementSettings.text || 'Your announcement text here'}
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '0 1rem' }}>
                  {announcementSettings.text || 'Your announcement text here'}
                </div>
              )}
            </div>
          </div>
        </Card>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="primary" disabled={loading} onClick={handleSaveSettings}>
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </>
    );
  };

  // ─── Reviews Tab ────────────────────────────────────────────────────────────
  const renderReviewsTab = () => (
    <>
      <Card style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ ...sectionHeadingStyle, marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
            <StarIcon size={20} color="#f59e0b" filled /> Customer Reviews
          </h3>
          <button
            onClick={openNewReviewForm}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#ffffff',
              backgroundColor: '#0d7463',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            <PlusIcon size={16} color="#ffffff" /> Add Review
          </button>
        </div>

        {reviewsLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading reviews...</div>
        ) : reviews.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280', fontSize: '0.875rem' }}>
            No reviews yet. Click "Add Review" to create your first customer review.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {reviews.map((review) => (
              <div
                key={review.id}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  padding: '1rem 1.25rem',
                  backgroundColor: '#fff',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.375rem' }}>
                      {review.customerImage && (
                        <img
                          src={review.customerImage}
                          alt={review.customerName}
                          style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                        />
                      )}
                      <span style={{ fontSize: '0.9375rem', fontWeight: '600', color: '#111827' }}>
                        {review.customerName}
                      </span>
                      <span
                        style={{
                          fontSize: '0.6875rem',
                          fontWeight: '600',
                          padding: '2px 8px',
                          borderRadius: '9999px',
                          backgroundColor: review.isActive !== false ? '#d1fae5' : '#fee2e2',
                          color: review.isActive !== false ? '#065f46' : '#991b1b',
                        }}
                      >
                        {review.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <StarRating rating={review.rating || 0} readonly size={16} />
                    <p style={{ fontSize: '0.875rem', color: '#4b5563', marginTop: '0.375rem', lineHeight: '1.5' }}>
                      {review.reviewText}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                    <button
                      onClick={() => handleToggleReviewActive(review)}
                      title={review.isActive !== false ? 'Deactivate' : 'Activate'}
                      style={{
                        padding: '0.375rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        color: review.isActive !== false ? '#991b1b' : '#065f46',
                      }}
                    >
                      {review.isActive !== false ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => openEditReviewForm(review)}
                      title="Edit"
                      style={{
                        padding: '0.375rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <EditIcon size={16} color="#4b5563" />
                    </button>
                    <button
                      onClick={() => handleDeleteReview(review.id)}
                      title="Delete"
                      style={{
                        padding: '0.375rem',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <TrashIcon size={16} color="#ef4444" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Review Form (Add/Edit) */}
      {reviewForm && (
        <Card style={{ marginBottom: '1.5rem', border: '2px solid #0d7463' }}>
          <h3 style={{ ...sectionHeadingStyle, borderBottomColor: '#0d7463' }}>
            <EditIcon size={20} color="#0d7463" /> {editingReviewId ? 'Edit Review' : 'Add New Review'}
          </h3>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Customer Name *</label>
            <Input
              type="text"
              value={reviewForm.customerName}
              onChange={(e) => setReviewForm(prev => ({ ...prev, customerName: e.target.value }))}
              placeholder="Enter customer name"
              style={{ width: '100%' }}
            />
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Rating *</label>
            <StarRating
              rating={reviewForm.rating}
              onChange={(val) => setReviewForm(prev => ({ ...prev, rating: val }))}
              size={28}
            />
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Review Text *</label>
            <textarea
              value={reviewForm.reviewText}
              onChange={(e) => setReviewForm(prev => ({ ...prev, reviewText: e.target.value }))}
              placeholder="Enter review text..."
              rows={4}
              style={{
                width: '100%',
                padding: '0.625rem 0.75rem',
                fontSize: '0.875rem',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                resize: 'vertical',
                fontFamily: 'inherit',
                lineHeight: '1.5',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Customer Image URL (optional)</label>
            <Input
              type="text"
              value={reviewForm.customerImage}
              onChange={(e) => setReviewForm(prev => ({ ...prev, customerImage: e.target.value }))}
              placeholder="https://example.com/photo.jpg"
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={closeReviewForm} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveReview} disabled={loading}>
              {loading ? 'Saving...' : editingReviewId ? 'Update Review' : 'Save Review'}
            </Button>
          </div>
        </Card>
      )}
    </>
  );

  // ─── Footer Tab ─────────────────────────────────────────────────────────────
  const renderFooterTab = () => (
    <>
      <Card style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ ...sectionHeadingStyle, marginBottom: '1rem' }}>
          <LinkIcon size={20} color="#0d7463" /> Social Media Links
        </h3>
        <p style={{ ...hintStyle, marginTop: '-0.5rem', marginBottom: '1.25rem' }}>
          Add the public profiles customers should see in your storefront footer. Leave a field empty to hide it.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '1rem',
        }}>
          {SOCIAL_LINK_FIELDS.map((field) => (
            <div key={field.key}>
              <label style={labelStyle}>{field.label}</label>
              <Input
                type="text"
                value={socialLinks[field.key] || ''}
                onChange={(e) => handleSocialLinkChange(field.key, e.target.value)}
                placeholder={field.placeholder}
                style={{ width: '100%' }}
              />
            </div>
          ))}
        </div>
      </Card>

      <Card style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ ...sectionHeadingStyle, marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
            <LinkIcon size={20} color="#0d7463" /> Footer Links
          </h3>
          <button
            onClick={addFooterLink}
            disabled={footerLinks.length >= 6}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: footerLinks.length >= 6 ? '#9ca3af' : '#ffffff',
              backgroundColor: footerLinks.length >= 6 ? '#e5e7eb' : '#0d7463',
              border: 'none',
              borderRadius: '8px',
              cursor: footerLinks.length >= 6 ? 'not-allowed' : 'pointer',
            }}
          >
            <PlusIcon size={16} color={footerLinks.length >= 6 ? '#9ca3af' : '#ffffff'} /> Add Footer Link
          </button>
        </div>

        {footerLinks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280', fontSize: '0.875rem' }}>
            No footer links configured. Click "Add Footer Link" to get started.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {footerLinks.map((link, index) => {
              const isExpanded = expandedFooterIndex === index;
              return (
                <div
                  key={index}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    backgroundColor: '#fff',
                  }}
                >
                  {/* Header row */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      backgroundColor: isExpanded ? '#f9fafb' : '#fff',
                    }}
                    onClick={() => setExpandedFooterIndex(isExpanded ? null : index)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                      {isExpanded ? <ChevronUpIcon size={16} color="#6b7280" /> : <ChevronDownIcon size={16} color="#6b7280" />}
                      <span style={{ fontSize: '0.9375rem', fontWeight: '500', color: '#111827' }}>
                        {link.title || 'Untitled Link'}
                      </span>
                      {link.content && !isExpanded && (
                        <span style={{ fontSize: '0.75rem', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>
                          — {link.content.substring(0, 50)}{link.content.length > 50 ? '...' : ''}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }} onClick={(e) => e.stopPropagation()}>
                      <ToggleSwitch
                        checked={link.enabled}
                        onChange={(val) => updateFooterLink(index, 'enabled', val)}
                      />
                      <button
                        onClick={() => removeFooterLink(index)}
                        style={{
                          padding: '0.375rem',
                          border: '1px solid #fecaca',
                          borderRadius: '6px',
                          backgroundColor: '#fff',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <TrashIcon size={16} color="#ef4444" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded editing area */}
                  {isExpanded && (
                    <div style={{ padding: '0 1rem 1rem 1rem', borderTop: '1px solid #e5e7eb' }}>
                      <div style={{ ...fieldGroupStyle, marginTop: '1rem' }}>
                        <label style={labelStyle}>Title</label>
                        <Input
                          type="text"
                          value={link.title}
                          onChange={(e) => updateFooterLink(index, 'title', e.target.value)}
                          placeholder="e.g. About Us, Refund Policy"
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div style={fieldGroupStyle}>
                        <label style={labelStyle}>Content</label>
                        <textarea
                          value={link.content}
                          onChange={(e) => updateFooterLink(index, 'content', e.target.value)}
                          placeholder="Enter the content for this page..."
                          rows={6}
                          style={{
                            width: '100%',
                            padding: '0.625rem 0.75rem',
                            fontSize: '0.875rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            resize: 'vertical',
                            fontFamily: 'inherit',
                            lineHeight: '1.5',
                            outline: 'none',
                            boxSizing: 'border-box',
                          }}
                        />
                        <p style={hintStyle}>
                          Supports plain text. This content will be displayed on a dedicated page linked from your storefront footer.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p style={{ ...hintStyle, marginTop: '1rem' }}>
          {footerLinks.length}/6 footer links configured
        </p>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" disabled={loading} onClick={handleSaveSettings}>
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </>
  );

  // ─── Newsletter Tab ─────────────────────────────────────────────────────────
  const renderNewsletterTab = () => {
    const layoutOptions = [
      { value: 'full', label: 'Full Width', desc: 'Centered form, no image' },
      { value: 'split-left', label: 'Image Left', desc: 'Image on left, form on right' },
      { value: 'split-right', label: 'Image Right', desc: 'Form on left, image on right' },
      { value: 'minimal', label: 'Minimal', desc: 'Compact, simple design' },
    ];

    const buttonStyleOptions = [
      { value: 'filled', label: 'Filled' },
      { value: 'outline', label: 'Outline' },
      { value: 'rounded', label: 'Rounded' },
    ];

    const animationOptions = [
      { value: 'fade', label: 'Fade In' },
      { value: 'slide-up', label: 'Slide Up' },
      { value: 'scale', label: 'Scale' },
    ];

    const isSplitLayout = newsletterSettings.layout === 'split-left' || newsletterSettings.layout === 'split-right';

    return (
      <>
        <Card style={{ marginBottom: '1.5rem' }}>
          <h3 style={sectionHeadingStyle}>
            <MailIcon size={20} color="#0d7463" /> Exit Popup Settings
          </h3>

          <div style={fieldGroupStyle}>
            <ToggleSwitch
              checked={newsletterSettings.enabled}
              onChange={(val) => handleNewsletterChange('enabled', val)}
              label="Enable Exit Popup"
            />
            <p style={hintStyle}>Show a popup when visitors try to leave your store</p>
          </div>

          {/* Layout Selection */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Layout Style</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
              {layoutOptions.map((opt) => (
                <div
                  key={opt.value}
                  onClick={() => handleNewsletterChange('layout', opt.value)}
                  style={{
                    padding: '1rem',
                    border: `2px solid ${newsletterSettings.layout === opt.value ? '#0d7463' : '#e5e7eb'}`,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    backgroundColor: newsletterSettings.layout === opt.value ? '#f0fdf4' : '#fff',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#111827', marginBottom: '0.25rem' }}>
                    {opt.label}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{opt.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Image Upload (for split layouts) */}
          {isSplitLayout && (
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Popup Image</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />

              {newsletterSettings.imageUrl ? (
                <div style={{
                  position: 'relative',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  marginBottom: '0.75rem',
                }}>
                  <img
                    src={newsletterSettings.imageUrl}
                    alt="Popup"
                    style={{
                      width: '100%',
                      height: '180px',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginTop: '0.75rem',
                  }}>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={mediaUploading}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.375rem',
                        padding: '0.5rem 1rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#374151',
                        backgroundColor: '#f3f4f6',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                      }}
                    >
                      <EditIcon size={16} color="#374151" />
                      Change
                    </button>
                    <button
                      onClick={openMediaPicker}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.375rem',
                        padding: '0.5rem 1rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#374151',
                        backgroundColor: '#f3f4f6',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                      }}
                    >
                      <GridIcon size={16} color="#374151" />
                      Gallery
                    </button>
                    <button
                      onClick={removeNewsletterImage}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0.5rem 1rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#ef4444',
                        backgroundColor: '#fef2f2',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                      }}
                    >
                      <TrashIcon size={16} color="#ef4444" />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    border: '2px dashed #d1d5db',
                    borderRadius: '12px',
                    padding: '2rem',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: '#f9fafb',
                    transition: 'all 0.2s',
                  }}
                >
                  {mediaUploading ? (
                    <div style={{ color: '#6b7280' }}>Uploading...</div>
                  ) : (
                    <>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        backgroundColor: '#0d7463',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 0.75rem',
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                      </div>
                      <p style={{ fontSize: '0.9375rem', fontWeight: '600', color: '#111827', marginBottom: '0.25rem' }}>
                        Upload Image
                      </p>
                      <p style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                        Click to select or{' '}
                        <span
                          onClick={(e) => { e.stopPropagation(); openMediaPicker(); }}
                          style={{ color: '#0d7463', fontWeight: '500', cursor: 'pointer' }}
                        >
                          choose from gallery
                        </span>
                      </p>
                    </>
                  )}
                </div>
              )}
              <p style={hintStyle}>Add an eye-catching image for your popup</p>
              <div style={{ marginTop: '0.5rem' }}>
                <ToggleSwitch
                  checked={newsletterSettings.imageOverlay}
                  onChange={(val) => handleNewsletterChange('imageOverlay', val)}
                  label="Add dark overlay to image"
                />
              </div>
            </div>
          )}
        </Card>

        {/* Content Settings */}
        <Card style={{ marginBottom: '1.5rem' }}>
          <h3 style={sectionHeadingStyle}>Content</h3>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Headline</label>
            <Input
              type="text"
              value={newsletterSettings.headline}
              onChange={(e) => handleNewsletterChange('headline', e.target.value)}
              placeholder="Stay in the loop!"
              style={{ width: '100%' }}
            />
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Subtext</label>
            <Input
              type="text"
              value={newsletterSettings.subtext}
              onChange={(e) => handleNewsletterChange('subtext', e.target.value)}
              placeholder="Get exclusive deals and updates"
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Button Text</label>
              <Input
                type="text"
                value={newsletterSettings.buttonText}
                onChange={(e) => handleNewsletterChange('buttonText', e.target.value)}
                placeholder="Subscribe"
              />
            </div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Dismiss Text</label>
              <Input
                type="text"
                value={newsletterSettings.dismissText}
                onChange={(e) => handleNewsletterChange('dismissText', e.target.value)}
                placeholder="No thanks"
              />
            </div>
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Success Message</label>
            <Input
              type="text"
              value={newsletterSettings.successMessage}
              onChange={(e) => handleNewsletterChange('successMessage', e.target.value)}
              placeholder="You're all set!"
              style={{ width: '100%' }}
            />
          </div>
        </Card>

        {/* Form Fields */}
        <Card style={{ marginBottom: '1.5rem' }}>
          <h3 style={sectionHeadingStyle}>Form Fields</h3>
          <p style={{ ...hintStyle, marginTop: '-1rem', marginBottom: '1rem' }}>Email is always required. Add optional fields below.</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <ToggleSwitch
                checked={newsletterSettings.showNameField}
                onChange={(val) => handleNewsletterChange('showNameField', val)}
                label="Collect name"
              />
              {newsletterSettings.showNameField && (
                <Input
                  type="text"
                  value={newsletterSettings.namePlaceholder}
                  onChange={(e) => handleNewsletterChange('namePlaceholder', e.target.value)}
                  placeholder="Name placeholder"
                  style={{ maxWidth: '200px' }}
                />
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <ToggleSwitch
                checked={newsletterSettings.showPhoneField}
                onChange={(val) => handleNewsletterChange('showPhoneField', val)}
                label="Collect phone"
              />
              {newsletterSettings.showPhoneField && (
                <Input
                  type="text"
                  value={newsletterSettings.phonePlaceholder}
                  onChange={(e) => handleNewsletterChange('phonePlaceholder', e.target.value)}
                  placeholder="Phone placeholder"
                  style={{ maxWidth: '200px' }}
                />
              )}
            </div>
          </div>
        </Card>

        {/* Discount Settings */}
        <Card style={{ marginBottom: '1.5rem' }}>
          <h3 style={sectionHeadingStyle}>Discount Offer</h3>

          <div style={fieldGroupStyle}>
            <ToggleSwitch
              checked={newsletterSettings.showDiscount}
              onChange={(val) => handleNewsletterChange('showDiscount', val)}
              label="Show discount code"
            />
            <p style={hintStyle}>Display a discount code to incentivize signups</p>
          </div>

          {newsletterSettings.showDiscount && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>Discount Code</label>
                  <Input
                    type="text"
                    value={newsletterSettings.discountCode}
                    onChange={(e) => handleNewsletterChange('discountCode', e.target.value.toUpperCase())}
                    placeholder="WELCOME10"
                    style={{ fontFamily: 'monospace', textTransform: 'uppercase' }}
                  />
                </div>
                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>Discount Label</label>
                  <Input
                    type="text"
                    value={newsletterSettings.discountLabel}
                    onChange={(e) => handleNewsletterChange('discountLabel', e.target.value)}
                    placeholder="Use code at checkout:"
                  />
                </div>
              </div>
            </>
          )}
        </Card>

        {/* Styling */}
        <Card style={{ marginBottom: '1.5rem' }}>
          <h3 style={sectionHeadingStyle}>Styling</h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.25rem' }}>
            <div>
              <label style={labelStyle}>Primary Color</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="color"
                  value={newsletterSettings.primaryColor}
                  onChange={(e) => handleNewsletterChange('primaryColor', e.target.value)}
                  style={{ width: '40px', height: '40px', border: '2px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', padding: '2px' }}
                />
                <Input
                  type="text"
                  value={newsletterSettings.primaryColor}
                  onChange={(e) => handleNewsletterChange('primaryColor', e.target.value)}
                  style={{ maxWidth: '100px', fontFamily: 'monospace', fontSize: '0.8rem' }}
                />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Background</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="color"
                  value={newsletterSettings.backgroundColor}
                  onChange={(e) => handleNewsletterChange('backgroundColor', e.target.value)}
                  style={{ width: '40px', height: '40px', border: '2px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', padding: '2px' }}
                />
                <Input
                  type="text"
                  value={newsletterSettings.backgroundColor}
                  onChange={(e) => handleNewsletterChange('backgroundColor', e.target.value)}
                  style={{ maxWidth: '100px', fontFamily: 'monospace', fontSize: '0.8rem' }}
                />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Text Color</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="color"
                  value={newsletterSettings.textColor}
                  onChange={(e) => handleNewsletterChange('textColor', e.target.value)}
                  style={{ width: '40px', height: '40px', border: '2px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', padding: '2px' }}
                />
                <Input
                  type="text"
                  value={newsletterSettings.textColor}
                  onChange={(e) => handleNewsletterChange('textColor', e.target.value)}
                  style={{ maxWidth: '100px', fontFamily: 'monospace', fontSize: '0.8rem' }}
                />
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Button Style</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {buttonStyleOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleNewsletterChange('buttonStyle', opt.value)}
                    style={{
                      flex: 1,
                      padding: '0.5rem 1rem',
                      fontSize: '0.8125rem',
                      fontWeight: newsletterSettings.buttonStyle === opt.value ? '600' : '500',
                      color: newsletterSettings.buttonStyle === opt.value ? '#fff' : '#374151',
                      backgroundColor: newsletterSettings.buttonStyle === opt.value ? '#0d7463' : '#f3f4f6',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Animation</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {animationOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleNewsletterChange('animation', opt.value)}
                    style={{
                      flex: 1,
                      padding: '0.5rem 1rem',
                      fontSize: '0.8125rem',
                      fontWeight: newsletterSettings.animation === opt.value ? '600' : '500',
                      color: newsletterSettings.animation === opt.value ? '#fff' : '#374151',
                      backgroundColor: newsletterSettings.animation === opt.value ? '#0d7463' : '#f3f4f6',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>
              Border Radius: {newsletterSettings.borderRadius}px
            </label>
            <input
              type="range"
              min="0"
              max="32"
              value={newsletterSettings.borderRadius}
              onChange={(e) => handleNewsletterChange('borderRadius', parseInt(e.target.value))}
              style={{ width: '100%', accentColor: '#0d7463' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#9ca3af' }}>
              <span>Square</span>
              <span>Rounded</span>
            </div>
          </div>
        </Card>

        {/* Behavior */}
        <Card style={{ marginBottom: '1.5rem' }}>
          <h3 style={sectionHeadingStyle}>Behavior</h3>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>
              Popup Delay: {newsletterSettings.delaySeconds} seconds
            </label>
            <input
              type="range"
              min="3"
              max="60"
              value={newsletterSettings.delaySeconds}
              onChange={(e) => handleNewsletterChange('delaySeconds', parseInt(e.target.value))}
              style={{ width: '100%', accentColor: '#0d7463' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#9ca3af' }}>
              <span>3s</span>
              <span>60s</span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <ToggleSwitch
              checked={newsletterSettings.showOnMobile}
              onChange={(val) => handleNewsletterChange('showOnMobile', val)}
              label="Show on mobile devices"
            />
            <ToggleSwitch
              checked={newsletterSettings.showOncePerSession}
              onChange={(val) => handleNewsletterChange('showOncePerSession', val)}
              label="Show only once per session"
            />
          </div>
        </Card>

        {/* Live Preview */}
        <Card style={{ marginBottom: '1.5rem' }}>
          <h3 style={sectionHeadingStyle}>Live Preview</h3>
          <div
            style={{
              maxWidth: isSplitLayout ? '600px' : '380px',
              margin: '0 auto',
              backgroundColor: newsletterSettings.backgroundColor,
              border: '2px solid #e5e7eb',
              borderRadius: `${newsletterSettings.borderRadius}px`,
              overflow: 'hidden',
              boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
              position: 'relative',
              display: isSplitLayout ? 'flex' : 'block',
              flexDirection: newsletterSettings.layout === 'split-right' ? 'row-reverse' : 'row',
            }}
          >
            {/* Image Section */}
            {isSplitLayout && (
              <div style={{
                flex: '0 0 45%',
                minHeight: '300px',
                backgroundColor: newsletterSettings.imageUrl ? 'transparent' : '#f1f5f9',
                backgroundImage: newsletterSettings.imageUrl ? `url(${newsletterSettings.imageUrl})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
              }}>
                {!newsletterSettings.imageUrl && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="M21 15l-5-5L5 21" />
                    </svg>
                  </div>
                )}
                {newsletterSettings.imageOverlay && newsletterSettings.imageUrl && (
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.1) 100%)' }} />
                )}
              </div>
            )}

            {/* Form Section */}
            <div style={{
              flex: 1,
              padding: isSplitLayout ? '2rem' : '2rem 1.5rem',
              textAlign: isSplitLayout ? 'left' : 'center',
            }}>
              {/* Close button */}
              <div
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '14px',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  color: '#9ca3af',
                  fontWeight: '600',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </div>

              {!isSplitLayout && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <MailIcon size={36} color={newsletterSettings.primaryColor} />
                </div>
              )}

              <h4 style={{ fontSize: '1.25rem', fontWeight: '700', color: newsletterSettings.textColor, marginBottom: '0.5rem' }}>
                {newsletterSettings.headline || 'Stay in the loop!'}
              </h4>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem', lineHeight: '1.5' }}>
                {newsletterSettings.subtext || 'Get exclusive deals and updates'}
              </p>

              {/* Discount badge */}
              {newsletterSettings.showDiscount && newsletterSettings.discountCode && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  backgroundColor: `${newsletterSettings.primaryColor}15`,
                  borderRadius: '6px',
                  marginBottom: '1rem',
                }}>
                  <span style={{ fontSize: '0.75rem', color: newsletterSettings.primaryColor, fontWeight: '500' }}>
                    {newsletterSettings.discountLabel}
                  </span>
                  <code style={{
                    padding: '2px 6px',
                    backgroundColor: newsletterSettings.primaryColor,
                    color: '#fff',
                    borderRadius: '4px',
                    fontWeight: '700',
                    fontSize: '0.7rem',
                  }}>
                    {newsletterSettings.discountCode}
                  </code>
                </div>
              )}

              {/* Form fields */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: isSplitLayout ? '100%' : '300px', margin: isSplitLayout ? 0 : '0 auto' }}>
                {newsletterSettings.showNameField && (
                  <div style={{
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    color: '#9ca3af',
                    textAlign: 'left',
                  }}>
                    {newsletterSettings.namePlaceholder || 'Your name'}
                  </div>
                )}
                <div style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.8rem',
                  color: '#9ca3af',
                  textAlign: 'left',
                }}>
                  your@email.com
                </div>
                {newsletterSettings.showPhoneField && (
                  <div style={{
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    color: '#9ca3af',
                    textAlign: 'left',
                  }}>
                    {newsletterSettings.phonePlaceholder || 'Phone number'}
                  </div>
                )}
                <div
                  style={{
                    padding: '0.625rem 1.25rem',
                    backgroundColor: newsletterSettings.buttonStyle === 'outline' ? 'transparent' : newsletterSettings.primaryColor,
                    color: newsletterSettings.buttonStyle === 'outline' ? newsletterSettings.primaryColor : '#ffffff',
                    border: newsletterSettings.buttonStyle === 'outline' ? `2px solid ${newsletterSettings.primaryColor}` : 'none',
                    borderRadius: newsletterSettings.buttonStyle === 'rounded' ? '50px' : '8px',
                    fontSize: '0.8125rem',
                    fontWeight: '600',
                    textAlign: 'center',
                  }}
                >
                  {newsletterSettings.buttonText || 'Subscribe'}
                </div>
              </div>
              <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.75rem' }}>
                {newsletterSettings.dismissText || 'No thanks'}
              </p>
            </div>
          </div>
        </Card>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="primary" disabled={loading} onClick={handleSaveSettings}>
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </>
    );
  };

  // ─── Render Active Tab ──────────────────────────────────────────────────────
  const renderActiveTab = () => {
    switch (activeTab) {
      case 'display':
        return renderDisplayTab();
      case 'announcement':
        return renderAnnouncementTab();
      case 'reviews':
        return renderReviewsTab();
      case 'footer':
        return renderFooterTab();
      case 'newsletter':
        return renderNewsletterTab();
      default:
        return renderDisplayTab();
    }
  };

  return (
    <Shell>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.5rem' }}>
            Storefront Settings
          </h1>
          <p style={{ fontSize: '1rem', color: '#6b7280' }}>
            Customize how your storefront looks and behaves for customers
          </p>
        </div>

        {renderTabBar()}
        {renderActiveTab()}
      </div>

      {/* Media Picker Modal */}
      <Modal
        isOpen={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        title="Select Image"
        size="large"
      >
        <div style={{ padding: '1rem' }}>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
            Choose an image from your media gallery
          </p>
          {(!mediaList || mediaList.length === 0) ? (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#6b7280',
              backgroundColor: '#f9fafb',
              borderRadius: '12px',
            }}>
              <p style={{ marginBottom: '0.5rem' }}>No images in your gallery yet</p>
              <p style={{ fontSize: '0.8125rem' }}>
                Upload an image using the upload button above
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: '0.75rem',
              maxHeight: '400px',
              overflowY: 'auto',
              padding: '0.25rem',
            }}>
              {mediaList.filter(m => m.type?.startsWith('image')).map((media) => (
                <div
                  key={media.id}
                  onClick={() => handleMediaSelect(media.url)}
                  style={{
                    position: 'relative',
                    aspectRatio: '1',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: newsletterSettings.imageUrl === media.url ? '3px solid #0d7463' : '2px solid transparent',
                    transition: 'all 0.2s',
                  }}
                >
                  <img
                    src={media.url}
                    alt={media.alt || media.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  {newsletterSettings.imageUrl === media.url && (
                    <div style={{
                      position: 'absolute',
                      top: '6px',
                      right: '6px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#0d7463',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
            marginTop: '1.5rem',
            paddingTop: '1rem',
            borderTop: '1px solid #e5e7eb',
          }}>
            <Button variant="secondary" onClick={() => setShowMediaPicker(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => fileInputRef.current?.click()}
            >
              Upload New
            </Button>
          </div>
        </div>
      </Modal>
    </Shell>
  );
};

export default StorefrontSettings;
